import Rainbow from 'rainbowvis.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  VenueMapColorSelector,
  VenueMapColorStyles,
} from 'src/components/Events/VenueMap/VenueMap.types';
import { useContent } from 'src/contexts/ContentContext';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Colors } from 'src/core/themes/constants/colors';
import { FormatOption } from 'src/modals/EditTableColumns';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';

import { OnSaleEventMetricsName } from '../OnSaleEvent.types';

const Spectrum = ['fff4d4', 'fdbc48', 'f3650b', 'd40530'];
const OnSaleEventHeatMapStepMin = 0;
const OnSaleEventHeatMapStepMax = 100;

type HeatMapColorSpectrum = {
  minValue: number;
  maxValue: number;
  sectionValue: Record<number, number>;
};

export const useOnSaleEventHeatMap = (isLocked?: boolean) => {
  const { metricsName } = useOnSaleEventsContext();
  const rainbowRef = useRef<Rainbow>(new Rainbow());
  const { isDarkMode } = useSiteTheme();

  const transactionsText = useContent(ContentId.Transactions);
  const clicksText = useContent(ContentId.Clicks);
  const conversionRateText = useContent(ContentId.BuyerConversionRate);
  const { sectionMetrics } = useOnSaleEventsContext();

  const dimensionValues = useMemo((): {
    transactions: HeatMapColorSpectrum;
    clicks: HeatMapColorSpectrum;
    conversionRate: HeatMapColorSpectrum;
  } => {
    let minTransactions = Number.MAX_SAFE_INTEGER,
      minConversionRate = 1,
      minClicks = Number.MAX_SAFE_INTEGER;
    let maxTransactions = -1,
      maxConversionRate = 0,
      maxClicks = -1;
    const sectionTransactions = {} as Record<number, number>;
    const sectionClicks = {} as Record<number, number>;
    const sectionConversionRate = {} as Record<number, number>;

    for (const {
      transactions,
      mapSectionClicks,
      pageVisits,
      sectionId,
    } of sectionMetrics) {
      if (transactions?.currentMetricsValue) {
        minTransactions = Math.min(
          minTransactions,
          transactions.currentMetricsValue
        );
        maxTransactions = Math.max(
          maxTransactions,
          transactions.currentMetricsValue
        );
        sectionTransactions[sectionId] = transactions.currentMetricsValue;
      }

      if (mapSectionClicks?.currentMetricsValue) {
        minClicks = Math.min(minClicks, mapSectionClicks.currentMetricsValue);
        maxClicks = Math.max(maxClicks, mapSectionClicks.currentMetricsValue);
        sectionClicks[sectionId] = mapSectionClicks.currentMetricsValue;
      }

      if (pageVisits?.currentMetricsValue) {
        const conversionRate =
          (transactions.currentMetricsValue ?? 0) /
          pageVisits.currentMetricsValue;
        minConversionRate = Math.min(minConversionRate, conversionRate);
        maxConversionRate = Math.max(maxConversionRate, conversionRate);
        sectionConversionRate[sectionId] = conversionRate;
      }
    }

    return {
      transactions: {
        minValue:
          minTransactions === Number.MAX_SAFE_INTEGER ? 0 : minTransactions,
        maxValue: maxTransactions === -1 ? 0 : maxTransactions,
        sectionValue: sectionTransactions,
      },
      clicks: {
        minValue: minClicks === Number.MAX_SAFE_INTEGER ? 0 : minClicks,
        maxValue: maxClicks === -1 ? 0 : maxClicks,
        sectionValue: sectionClicks,
      },
      conversionRate: {
        minValue: minConversionRate === 1 ? 0 : minConversionRate,
        maxValue: maxConversionRate,
        sectionValue: sectionConversionRate,
      },
    };
  }, [sectionMetrics]);

  const spectrumValues = useMemo(() => {
    if (metricsName === OnSaleEventMetricsName.Clicks) {
      return dimensionValues.clicks;
    }
    if (metricsName === OnSaleEventMetricsName.ConversionRate) {
      return dimensionValues.conversionRate;
    }
    return dimensionValues.transactions;
  }, [dimensionValues, metricsName]);

  useEffect(() => {
    rainbowRef.current.setNumberRange(
      OnSaleEventHeatMapStepMin,
      OnSaleEventHeatMapStepMax
    );
    rainbowRef.current.setSpectrumByArray(Spectrum);
  }, []);

  const colorBandProps = useMemo(() => {
    const minCount = applyNumberFormatting({
      inputNumber: spectrumValues.minValue,
      isPercent: metricsName === OnSaleEventMetricsName.ConversionRate,
      precision:
        metricsName !== OnSaleEventMetricsName.ConversionRate
          ? FormatOption.Whole
          : undefined,
    });
    const maxCount = applyNumberFormatting({
      inputNumber: spectrumValues.maxValue,
      isPercent: metricsName === OnSaleEventMetricsName.ConversionRate,
      precision:
        metricsName !== OnSaleEventMetricsName.ConversionRate
          ? FormatOption.Whole
          : undefined,
    });
    const metricsText =
      metricsName === OnSaleEventMetricsName.Transactions
        ? transactionsText
        : metricsName === OnSaleEventMetricsName.ConversionRate
          ? conversionRateText
          : clicksText;
    return {
      numOfSteps: OnSaleEventHeatMapStepMax - OnSaleEventHeatMapStepMin + 1,
      spectrum: Spectrum,
      labels: [`${metricsText}: ${minCount}`, `${maxCount}`],
    };
  }, [
    spectrumValues.minValue,
    spectrumValues.maxValue,
    metricsName,
    transactionsText,
    conversionRateText,
    clicksText,
  ]);

  const defaultSectionStyle = useMemo(
    (): VenueMapColorStyles => ({
      fill: isDarkMode
        ? vars.color.backgroundSecondary
        : vars.color.backgroundPrimary,
      stroke: isDarkMode ? vars.color.backgroundPrimary : vars.color.border,
      textColor: vars.color.textPrimaryLight,
    }),
    [isDarkMode]
  );

  const unLockedSectionStyleOverride = useMemo(
    (): Partial<VenueMapColorStyles> => ({
      stroke: isDarkMode ? vars.color.backgroundPrimary : Colors.black,
      textColor: Colors.black,
    }),
    [isDarkMode]
  );

  const getColor = useCallback(
    (selector: VenueMapColorSelector): VenueMapColorStyles => {
      if (isLocked) {
        return defaultSectionStyle;
      }
      const { minValue, maxValue, sectionValue } = spectrumValues;
      const value = sectionValue[selector.sectionId];

      if (!value) {
        return defaultSectionStyle;
      }

      // Default score to median, 50
      const normalizedScore =
        maxValue === 0 ? 50 : ((value - minValue) / maxValue) * 100;

      const color = rainbowRef.current.colorAt(normalizedScore);
      return {
        ...defaultSectionStyle,
        fill: `#${color}`,
        ...unLockedSectionStyleOverride,
      };
    },
    [
      defaultSectionStyle,
      isLocked,
      spectrumValues,
      unLockedSectionStyleOverride,
    ]
  );

  return {
    colorBandProps,
    getColor,
  };
};
